import { type ReactNode, useLayoutEffect, useRef } from "react"

import { cn } from "@/lib/cn"

/**
 * Los primitivos de tabla. **Única fuente de verdad del estilo de toda tabla.**
 *
 * ⚠ **Antes había doce tablas y doce estilos.** Cada pantalla repetía su `<table>`
 * con su propio relleno, su propio color de cabecera y su propio borde, y bastaba
 * escribir una nueva para que se separara del resto sin que nadie lo notara — no
 * falla nada, simplemente el sistema deja de parecer un sistema. El patrón es el de
 * `constructora_v2`: componentes canónicos en vez de una clase que hay que recordar
 * aplicar.
 *
 * ⚠ **`Td` pide `numero` para las celdas con cifras**, y eso no es cosmética: sin
 * `tabular-nums` los dígitos tienen anchos distintos y las columnas bailan al
 * paginar (regla 10). Que sea una prop y no una clase suelta es lo que hace que se
 * vea al leer la fila si falta.
 *
 * ⚠ **La transición de la fila es de HOVER, no de datos.** Ninguna fila se anima al
 * releer: el sondeo de 60 s haría temblar la tabla cada minuto y esta gente la mira
 * ocho horas (regla 11).
 *
 * Uso:
 *
 * ```tsx
 * <Tabla>
 *   <Cabecera><Th>Código</Th><Th derecha>Monto</Th></Cabecera>
 *   <Cuerpo>
 *     {filas.length === 0 ? (
 *       <TablaVacia colSpan={2}>Todavía no hay nada</TablaVacia>
 *     ) : (
 *       filas.map((f) => (
 *         <Fila key={f.id}><Td numero>{f.codigo}</Td><Td numero derecha>{f.monto}</Td></Fila>
 *       ))
 *     )}
 *   </Cuerpo>
 * </Tabla>
 * ```
 */

/**
 * ⚠ **En el teléfono, cada fila es una TARJETA** (`.tabla-apilable` en `globales.css`,
 * por debajo de 640 px): la primera celda hace de título y las demás salen en
 * renglones «COLUMNA · valor». Con ocho columnas, la tabla obligaba a deslizar de
 * lado para ver el importe de una orden, que es justo lo que se va a mirar.
 *
 * ⚠ **El rótulo de cada celda lo pone ESTE componente, leyendo la cabecera.** Así
 * las tablas del sistema se apilan sin que ninguna pantalla repita sus títulos en
 * cada `Td`, y una columna nueva no se puede olvidar de llevarlo. Se recalcula
 * cuando cambian las filas (el sondeo, una tanda más) y respeta `colSpan`: una celda
 * que abarca varias columnas no tiene un rótulo que poner y va a lo ancho.
 *
 * `apilar={false}` para una tabla que sólo se entiende como cuadrícula.
 */
function useRotulosDeCabecera(tabla: React.RefObject<HTMLTableElement | null>, activo: boolean) {
  useLayoutEffect(() => {
    const t = tabla.current
    if (t === null || !activo) return
    const rotular = () => {
      const titulos = [...t.querySelectorAll("thead th")].map((th) => th.textContent?.trim() ?? "")
      for (const fila of t.querySelectorAll("tbody tr")) {
        let columna = 0
        for (const celda of fila.children) {
          const ancho = (celda as HTMLTableCellElement).colSpan || 1
          const rotulo = ancho === 1 ? (titulos[columna] ?? "") : ""
          // Sólo si cambia: escribir el mismo valor en cada fila de cada sondeo es
          // trabajo para nada (y el observador no mira atributos, así que no se
          // dispara a sí mismo).
          if (celda.getAttribute("data-rotulo") !== rotulo)
            celda.setAttribute("data-rotulo", rotulo)
          columna += ancho
        }
      }
    }
    rotular()
    const observador = new MutationObserver(rotular)
    observador.observe(t, { childList: true, subtree: true })
    return () => observador.disconnect()
  }, [tabla, activo])
}

export function Tabla({
  children,
  etiqueta,
  className,
  apilar = true,
  exportar,
}: {
  children: ReactNode
  /** Título de la tabla al exportarla, si no es su `etiqueta`; `false` la deja fuera. */
  exportar?: string | false
  /** Descripción para lectores de pantalla. Sale como `<caption>` oculto. */
  etiqueta?: string
  className?: string
  /** En el teléfono, cada fila como tarjeta. Ver arriba. */
  apilar?: boolean
}) {
  const tabla = useRef<HTMLTableElement | null>(null)
  useRotulosDeCabecera(tabla, apilar)
  return (
    // El contenedor recorta y hace el desplazamiento horizontal: una tabla ancha
    // nunca puede hacer que se desplace la página entera.
    <div className="superficie overflow-x-auto">
      <table
        ref={tabla}
        className={cn("w-full text-sm", apilar && "tabla-apilable", className)}
        data-exportar={exportar === false ? "no" : undefined}
        data-exportar-titulo={typeof exportar === "string" ? exportar : undefined}
      >
        {etiqueta !== undefined && <caption className="sr-only">{etiqueta}</caption>}
        {children}
      </table>
    </div>
  )
}

export function Cabecera({ children }: { children: ReactNode }) {
  return (
    <thead>
      {/* La cabecera sube la luz un punto sobre el cristal de la tabla, como la pista
          de las pestañas: un gris opaco encima sería un parche (ver «El cristal» en
          `.claude/rules/frontend.md`). */}
      <tr
        className="border-b"
        style={{ borderColor: "var(--cristal-filo)", backgroundColor: "var(--cristal-relleno)" }}
      >
        {children}
      </tr>
    </thead>
  )
}

export function Th({
  children,
  derecha,
  className,
  exportar = true,
}: {
  children?: ReactNode
  derecha?: boolean
  className?: string
  /** `false`: la columna no sale al exportar (ver `componentes/exportar/desdeTabla.ts`). */
  exportar?: boolean
}) {
  return (
    <th
      scope="col"
      data-exportar={exportar ? undefined : "no"}
      className={cn(
        "px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap",
        derecha ? "text-right" : "text-left",
        className,
      )}
      style={{ color: "var(--fg-muted)" }}
    >
      {children}
    </th>
  )
}

export function Cuerpo({ children }: { children: ReactNode }) {
  // Separadores finos entre filas, no un borde completo por celda: la retícula
  // compite con las cifras y una tabla de dinero se lee por sus columnas.
  //
  // ⚠ **El color del separador lo pone `Fila`, no esto.** Aquí había un
  // `borderColor` en el `<tbody>` que no hacía nada: `border-color` NO se hereda en
  // CSS, así que cada `<tr>` caía en su valor inicial —`currentColor`, o sea el
  // color del texto— y los separadores de TODAS las tablas del sistema salían casi
  // negros en vez de gris `--border`. Se veía como una raya dura entre filas y nadie
  // lo relacionaba con esta línea.
  return <tbody className="divide-y">{children}</tbody>
}

export function Fila({
  children,
  className,
  atenuada,
  pegadaALaSiguiente,
  style,
  ...resto
}: {
  children: ReactNode
  className?: string
  /** Para lo anulado: sigue visible y legible, sólo apagado. */
  atenuada?: boolean
  /**
   * Calla el separador con la fila de ABAJO: las dos son **la misma cosa**.
   *
   * ⚠ Es lo que hace legible un árbol dentro de una tabla. Sin esto, el separador
   * corta la línea que baja de la madre a la hija y las dos vuelven a leerse como
   * dos elementos sueltos — justo lo que el árbol venía a evitar.
   *
   * ⚠ **Abajo y no arriba, y eso no es un detalle.** Tailwind v4 cambió `divide-y`:
   * ahora es `border-bottom` sobre `:not(:last-child)`, no `border-top` sobre los
   * hermanos como en la v3. Callar el borde superior de la hija —que es lo que
   * parece— no quita nada, porque la raya es del borde inferior de la madre.
   */
  pegadaALaSiguiente?: boolean
} & React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      // El roce es el del cristal —la luz sube un punto— y no un gris opaco encima.
      className={cn("toque-cristal transition-colors", className)}
      style={{
        // El color del separador va aquí porque `border-color` no se hereda del
        // `<tbody>`: sin esto cada fila usa `currentColor` y la raya sale negra.
        borderColor: "var(--cristal-filo)",
        opacity: atenuada ? 0.55 : undefined,
        borderBottomWidth: pegadaALaSiguiente ? 0 : undefined,
        ...style,
      }}
      {...resto}
    >
      {children}
    </tr>
  )
}

export function Td({
  children,
  className,
  derecha,
  numero,
  colSpan,
  ...resto
}: {
  children?: ReactNode
  className?: string
  derecha?: boolean
  /** Cifras, cantidades o fechas: alinea los dígitos. Regla 10. */
  numero?: boolean
  colSpan?: number
} & React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      colSpan={colSpan}
      className={cn(
        "px-4 py-2.5 align-top",
        derecha && "text-right",
        numero && "tabular-nums",
        className,
      )}
      {...resto}
    >
      {children}
    </td>
  )
}

export function TablaVacia({ children, colSpan = 99 }: { children: ReactNode; colSpan?: number }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-4 py-10 text-center text-sm"
        style={{ color: "var(--fg-muted)" }}
      >
        {children}
      </td>
    </tr>
  )
}
